import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Activity,
  AlertCircle,
  ArrowDownToLine,
  CheckCircle2,
  Database,
  HeartPulse,
  RefreshCw,
  Server,
  XCircle,
  Cpu,
  Wifi,
  ChevronDown,
} from 'lucide-react'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from 'recharts'
import { api, type KongHealthResponse, type RouteMetrics, type LatencyBucket } from '@services/api'

// ─── Ring-buffer snapshot types ───────────────────────────────────────────

interface RequestRateSnapshot {
  time: string
  rate: number
  rate2xx: number
  rate4xx: number
  rate429: number
  rate5xx: number
}

const POLL_INTERVAL_MS = 15_000
const MAX_HISTORY = 60

// Health check routes to exclude from aggregate stats (graphs, cards, error rate)
const HEALTH_CHECK_ROUTES = new Set(['dalify-health-route', 'kong-health-route'])
const isHealthCheck = (route: string) => HEALTH_CHECK_ROUTES.has(route)

// ─── Helpers ──────────────────────────────────────────────────────────────

function fmt(n: number, decimals = 1): string {
  return n.toFixed(decimals)
}

function fmtBytes(bytes: number): string {
  if (bytes >= 1_073_741_824) return `${fmt(bytes / 1_073_741_824)} GB`
  if (bytes >= 1_048_576) return `${fmt(bytes / 1_048_576)} MB`
  if (bytes >= 1_024) return `${fmt(bytes / 1_024)} KB`
  return `${bytes} B`
}

function fmtMs(ms: number): string {
  if (ms === 0) return '—'
  return ms < 1 ? '<1 ms' : `${fmt(ms)} ms`
}

function nowLabel(): string {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function shortRoute(name: string): string {
  // Abbreviate long route names for chart axes
  return name.replace(/^dalify-/, '').replace(/-route$/, '')
}

/** Request-weighted average of a latency percentile across routes. */
function filteredTotalsWeighted(routes: RouteMetrics[], key: 'p50Ms' | 'p95Ms' | 'p99Ms'): number {
  const totalReqs = routes.reduce((a, r) => a + r.requests, 0)
  if (totalReqs === 0) return 0
  return routes.reduce((a, r) => a + r[key] * r.requests, 0) / totalReqs
}

// ─── Sub-components ───────────────────────────────────────────────────────

const StatusDot: React.FC<{ ok: boolean | null; label: string }> = ({ ok, label }) => {
  const color = ok === null ? 'bg-gray-400 dark:bg-gray-500' : ok ? 'bg-emerald-500' : 'bg-red-500'
  const text =
    ok === null
      ? 'text-gray-500 dark:text-gray-400'
      : ok
        ? 'text-emerald-700 dark:text-emerald-300'
        : 'text-red-700 dark:text-red-300'
  const Icon = ok === null ? Activity : ok ? CheckCircle2 : XCircle
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${text}`}>
      <span className={`h-2 w-2 rounded-full ${color}`} />
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  )
}

const StatCard: React.FC<{
  icon: React.ReactNode
  label: string
  value: string
  sub?: string
  bg?: string
}> = ({ icon, label, value, sub, bg = 'bg-white dark:bg-gray-800' }) => (
  <div className={`card flex items-start gap-4 p-4 ${bg}`}>
    <div className="mt-0.5 shrink-0">{icon}</div>
    <div className="min-w-0">
      <p className="truncate text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{sub}</p>}
    </div>
  </div>
)

const UpstreamBadge: React.FC<{ state: string }> = ({ state }) => {
  const map: Record<string, { cls: string; label: string }> = {
    healthy: { cls: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300', label: 'Healthy' },
    unhealthy: { cls: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300', label: 'Unhealthy' },
    dns_error: { cls: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300', label: 'DNS Error' },
    unknown: { cls: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400', label: 'Unknown' },
  }
  const m = map[state] ?? map['unknown']
  return <span className={`rounded px-2 py-0.5 text-xs font-semibold ${m.cls}`}>{m.label}</span>
}

// ─── Request rate chart ────────────────────────────────────────────────────

const RequestRateChart: React.FC<{ history: RequestRateSnapshot[] }> = ({ history }) => (
  <div className="card p-4">
    <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Request Rate (per poll interval)</h3>
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={history} margin={{ top: 4, right: 8, bottom: 32, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
        <YAxis tick={{ fontSize: 10 }} width={40} />
        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
        <Line type="monotone" dataKey="rate" name="Total" stroke="#6366f1" dot={false} strokeWidth={2} />
        <Line type="monotone" dataKey="rate2xx" name="2xx" stroke="#10b981" dot={false} strokeWidth={1.5} />
        <Line type="monotone" dataKey="rate429" name="429" stroke="#f59e0b" dot={false} strokeWidth={1.5} />
        <Line type="monotone" dataKey="rate4xx" name="4xx" stroke="#fb923c" dot={false} strokeWidth={1.5} />
        <Line type="monotone" dataKey="rate5xx" name="5xx" stroke="#ef4444" dot={false} strokeWidth={1.5} />
      </LineChart>
    </ResponsiveContainer>
  </div>
)

// ─── Per-route latency comparison bar chart ────────────────────────────────

type LatencyMode = 'request' | 'upstream' | 'kong'

const RouteLatencyChart: React.FC<{ routes: RouteMetrics[] }> = ({ routes }) => {
  const [mode, setMode] = useState<LatencyMode>('request')

  const data = routes
    .filter((r) => {
      if (mode === 'request') return r.p95Ms > 0
      if (mode === 'upstream') return r.upstreamP95Ms > 0
      return r.kongP95Ms > 0
    })
    .map((r) => ({
      route: shortRoute(r.route),
      full: r.route,
      p50: mode === 'request' ? r.p50Ms : mode === 'upstream' ? r.upstreamP50Ms : r.kongP50Ms,
      p95: mode === 'request' ? r.p95Ms : mode === 'upstream' ? r.upstreamP95Ms : r.kongP95Ms,
      p99: mode === 'request' ? r.p99Ms : mode === 'upstream' ? r.upstreamP99Ms : r.kongP99Ms,
      mean: mode === 'request' ? r.meanMs : mode === 'upstream' ? r.upstreamMeanMs : r.kongMeanMs,
    }))
    .sort((a, b) => b.p95 - a.p95)

  const titles: Record<LatencyMode, string> = {
    request: 'Total Request Latency',
    upstream: 'Upstream Latency',
    kong: 'Kong Processing Overhead',
  }

  return (
    <div className="card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{titles[mode]} — per Route (ms)</h3>
        <div className="flex gap-1 rounded-lg border border-gray-200 p-0.5 dark:border-gray-700">
          {(['request', 'upstream', 'kong'] as LatencyMode[]).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                mode === m
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>
      {data.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">
          No latency data yet. Send some requests through Kong.
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(160, data.length * 36)}>
          <BarChart data={data} layout="vertical" margin={{ top: 0, right: 16, bottom: 0, left: 64 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 10 }} unit=" ms" />
            <YAxis type="category" dataKey="route" tick={{ fontSize: 10 }} width={96} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
              formatter={(v, name) => [`${fmt(Number(v))} ms`, name]}
              labelFormatter={(label, payload) => payload?.[0]?.payload?.full ?? label}
            />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="p50" name="p50" fill="#a5b4fc" radius={[0, 2, 2, 0]} />
            <Bar dataKey="p95" name="p95" fill="#6366f1" radius={[0, 2, 2, 0]} />
            <Bar dataKey="p99" name="p99" fill="#4338ca" radius={[0, 2, 2, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

// ─── Route histogram inspector ─────────────────────────────────────────────

type HistogramType = 'request' | 'kong'

const RouteHistogram: React.FC<{ routes: RouteMetrics[] }> = ({ routes }) => {
  const [selectedRoute, setSelectedRoute] = useState<string>('')
  const [type, setType] = useState<HistogramType>('request')

  const routeOptions = routes.filter(
    (r) => r.requestBuckets.some((b) => b.count > 0) || r.kongBuckets.some((b) => b.count > 0),
  )

  const route =
    routes.find((r) => r.route === selectedRoute) ??
    routeOptions.find((r) => r.route === 'dalify-api-route') ??
    routeOptions[0]
  const buckets: LatencyBucket[] = type === 'request' ? (route?.requestBuckets ?? []) : (route?.kongBuckets ?? [])

  const chartData = buckets
    .filter((b) => b.count > 0)
    .map((b) => ({ le: b.le <= 1000 ? `${b.le}` : b.le >= 1000 ? `${b.le / 1000}s` : `${b.le}`, count: b.count }))

  const totalInBuckets = buckets.reduce((s, b) => s + b.count, 0)

  return (
    <div className="card p-4">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Latency Distribution</h3>
        <div className="relative">
          <select
            value={route?.route ?? ''}
            onChange={(e) => setSelectedRoute(e.target.value)}
            className="appearance-none rounded-lg border border-gray-200 bg-white py-1 pr-8 pl-3 text-xs text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
          >
            {routeOptions.map((r) => (
              <option key={r.route} value={r.route}>
                {r.route}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute top-1/2 right-2 h-3 w-3 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
        </div>
        <div className="flex gap-1 rounded-lg border border-gray-200 p-0.5 dark:border-gray-700">
          {(['request', 'kong'] as HistogramType[]).map((t) => (
            <button
              key={t}
              onClick={() => setType(t)}
              className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                type === t
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t === 'request' ? 'Request' : 'Kong overhead'}
            </button>
          ))}
        </div>
        {totalInBuckets > 0 && (
          <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
            {totalInBuckets.toLocaleString()} samples
          </span>
        )}
      </div>
      {chartData.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">
          No histogram data for this route/type.
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="le"
              tick={{ fontSize: 10 }}
              label={{ value: 'ms (bucket ≤)', position: 'insideBottomRight', offset: -4, fontSize: 10 }}
            />
            <YAxis tick={{ fontSize: 10 }} width={48} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
              formatter={(v) => [`${Number(v).toLocaleString()} requests`]}
              labelFormatter={(le) => `Bucket ≤ ${le} ms`}
            />
            <Bar dataKey="count" name="Requests" radius={[2, 2, 0, 0]}>
              {chartData.map((_, i) => {
                const ratio = i / Math.max(chartData.length - 1, 1)
                const r = Math.round(99 + ratio * (239 - 99))
                const g = Math.round(102 + ratio * (68 - 102))
                const b = Math.round(241 + ratio * (68 - 241))
                return <Cell key={i} fill={`rgb(${r},${g},${b})`} />
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

// ─── Route table ──────────────────────────────────────────────────────────

type SortKey = 'route' | 'requests' | 'p95Ms' | 'requests5xx' | 'requests429'

const RouteTable: React.FC<{ routes: RouteMetrics[] }> = ({ routes }) => {
  const [sort, setSort] = useState<SortKey>('requests')
  const [asc, setAsc] = useState(false)

  const toggle = (key: SortKey) => {
    if (sort === key) setAsc(!asc)
    else {
      setSort(key)
      setAsc(false)
    }
  }

  const sorted = [...routes].sort((a, b) => {
    const av = sort === 'route' ? a.route : Number(a[sort])
    const bv = sort === 'route' ? b.route : Number(b[sort])
    if (av < bv) return asc ? -1 : 1
    if (av > bv) return asc ? 1 : -1
    return 0
  })

  const Th: React.FC<{ k: SortKey; label: string; className?: string }> = ({ k, label, className = '' }) => (
    <th
      onClick={() => toggle(k)}
      className={`cursor-pointer px-3 py-2 text-left text-xs font-semibold whitespace-nowrap text-gray-500 select-none hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 ${className}`}
    >
      {label} {sort === k ? (asc ? '↑' : '↓') : ''}
    </th>
  )

  return (
    <div className="card overflow-hidden">
      <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Route Details</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-white/5">
            <tr>
              <Th k="route" label="Route" />
              <Th k="requests" label="Total" />
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">2xx</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">4xx</th>
              <Th k="requests429" label="429" className="text-amber-600" />
              <Th k="requests5xx" label="5xx" className="text-red-600" />
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">Mean</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">p50</th>
              <Th k="p95Ms" label="p95" />
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">p99</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">Up p95</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-amber-600">Kong p95</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">In</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">Out</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr
                key={r.route}
                className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-900"
              >
                <td className="px-3 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">{r.route}</td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.requests.toLocaleString()}</td>
                <td className="px-3 py-2 text-emerald-600">{r.requests2xx.toLocaleString()}</td>
                <td className="px-3 py-2 text-orange-500">{r.requests4xx.toLocaleString()}</td>
                <td
                  className={`px-3 py-2 font-medium ${r.requests429 > 0 ? 'text-amber-600' : 'text-gray-400 dark:text-gray-500'}`}
                >
                  {r.requests429.toLocaleString()}
                </td>
                <td
                  className={`px-3 py-2 font-medium ${r.requests5xx > 0 ? 'text-red-600' : 'text-gray-400 dark:text-gray-500'}`}
                >
                  {r.requests5xx.toLocaleString()}
                </td>
                <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{fmtMs(r.meanMs)}</td>
                <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{fmtMs(r.p50Ms)}</td>
                <td className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">{fmtMs(r.p95Ms)}</td>
                <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{fmtMs(r.p99Ms)}</td>
                <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{fmtMs(r.upstreamP95Ms)}</td>
                <td className="px-3 py-2 text-amber-600">{fmtMs(r.kongP95Ms)}</td>
                <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{fmtBytes(r.bandwidthIngressBytes)}</td>
                <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{fmtBytes(r.bandwidthEgressBytes)}</td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={14} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                  No route metrics yet. Send requests through Kong to populate.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Main page ─────────────────────────────────────────────────────────────

const KongHealth: React.FC = () => {
  const [data, setData] = useState<KongHealthResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [rateHistory, setRateHistory] = useState<RequestRateSnapshot[]>([])

  const prevRef = useRef<{ total: number; r2: number; r4: number; r429: number; r5: number } | null>(null)

  const push = useCallback((fresh: KongHealthResponse) => {
    const t = nowLabel()
    // Compute totals excluding health check routes for the rate chart
    const filtered = fresh.routes.filter((r) => !isHealthCheck(r.route))
    const fTotal = filtered.reduce((a, r) => a + r.requests, 0)
    const f2xx = filtered.reduce((a, r) => a + r.requests2xx, 0)
    const f4xx = filtered.reduce((a, r) => a + r.requests4xx, 0)
    const f429 = filtered.reduce((a, r) => a + r.requests429, 0)
    const f5xx = filtered.reduce((a, r) => a + r.requests5xx, 0)

    setRateHistory((prev) => {
      const p = prevRef.current
      const rate = p ? Math.max(0, fTotal - p.total) : 0
      const rate2xx = p ? Math.max(0, f2xx - p.r2) : 0
      const rate4xx = p ? Math.max(0, f4xx - p.r4) : 0
      const rate429 = p ? Math.max(0, f429 - p.r429) : 0
      const rate5xx = p ? Math.max(0, f5xx - p.r5) : 0
      prevRef.current = { total: fTotal, r2: f2xx, r4: f4xx, r429: f429, r5: f5xx }
      return [...prev.slice(-(MAX_HISTORY - 1)), { time: t, rate, rate2xx, rate4xx, rate429, rate5xx }]
    })
  }, [])

  const load = useCallback(async () => {
    try {
      const fresh = await api.adminGetKongHealth()
      setData(fresh)
      push(fresh)
      setError(null)
    } catch {
      setError('Failed to reach Kong health endpoint.')
    } finally {
      setLoading(false)
    }
  }, [push])

  useEffect(() => {
    void load()
    const id = setInterval(() => void load(), POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [load])

  const dbOk = data ? data.status.database === 'reachable' : null
  const unhealthyCount = data?.upstreams.filter((u) => u.state !== 'healthy').length ?? 0

  // Filtered routes (exclude health checks) for aggregate stats
  const filteredRoutes = data?.routes.filter((r) => !isHealthCheck(r.route)) ?? []
  const filteredRequests = filteredRoutes.reduce((a, r) => a + r.requests, 0)
  const filteredTotals = {
    requests: filteredRequests,
    requests2xx: filteredRoutes.reduce((a, r) => a + r.requests2xx, 0),
    requests4xx: filteredRoutes.reduce((a, r) => a + r.requests4xx, 0),
    requests429: filteredRoutes.reduce((a, r) => a + r.requests429, 0),
    requests5xx: filteredRoutes.reduce((a, r) => a + r.requests5xx, 0),
    bandwidthIngressBytes: filteredRoutes.reduce((a, r) => a + r.bandwidthIngressBytes, 0),
    bandwidthEgressBytes: filteredRoutes.reduce((a, r) => a + r.bandwidthEgressBytes, 0),
    // Weighted-average latency percentiles (approximation excluding health checks)
    p50Ms: filteredTotalsWeighted(filteredRoutes, 'p50Ms'),
    p95Ms: filteredTotalsWeighted(filteredRoutes, 'p95Ms'),
    p99Ms: filteredTotalsWeighted(filteredRoutes, 'p99Ms'),
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 dark:bg-emerald-900/40">
            <HeartPulse className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Kong Health</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Live metrics · polls every {POLL_INTERVAL_MS / 1000}s
              {data && (
                <span className="ml-2 text-gray-400 dark:text-gray-500">
                  · {new Date(data.fetchedAt).toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={() => void load()}
          disabled={loading}
          className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-900"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800/50 dark:bg-red-900/30">
          <AlertCircle className="h-5 w-5 shrink-0 text-red-500" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {loading && !data && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-xl border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-700"
            />
          ))}
        </div>
      )}

      {data && (
        <>
          {/* ── Node status strip ── */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-gray-200 bg-white px-5 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
            <StatusDot ok={dbOk} label={`DB ${data.status.database}`} />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              <Server className="mr-1 inline h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
              Kong {data.kongVersion}
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              <Cpu className="mr-1 inline h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
              {data.status.workerCount} workers
              {data.status.pid > 0 && (
                <span className="text-gray-400 dark:text-gray-500"> · PID {data.status.pid}</span>
              )}
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              <Wifi className="mr-1 inline h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
              {data.status.connectionsActive} active
              <span className="text-gray-400 dark:text-gray-500">
                {' '}
                ({data.status.connectionsReading}r · {data.status.connectionsWriting}w ·{' '}
                {data.status.connectionsWaiting} waiting)
              </span>{' '}
              / {data.status.connectionsTotal} total
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              <Database className="mr-1 inline h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
              {data.status.memoryLuaUsedKb > 0
                ? `${data.status.memoryLuaUsedKb.toLocaleString()} KB shared · ${data.status.workerVmMb} MB workers`
                : 'Memory n/a'}
            </span>
            {unhealthyCount > 0 && (
              <span className="ml-auto flex items-center gap-1 text-sm font-semibold text-red-600">
                <XCircle className="h-4 w-4" />
                {unhealthyCount} upstream{unhealthyCount > 1 ? 's' : ''} unhealthy
              </span>
            )}
            {unhealthyCount === 0 && data.upstreams.length > 0 && (
              <span className="ml-auto flex items-center gap-1 text-sm text-emerald-600">
                <CheckCircle2 className="h-4 w-4" />
                All upstreams healthy
              </span>
            )}
          </div>

          {/* ── Stat cards (excludes health check routes) ── */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard
              icon={<Activity className="h-5 w-5 text-indigo-500" />}
              label="Total Requests"
              value={filteredTotals.requests.toLocaleString()}
              sub={`${filteredTotals.requests2xx.toLocaleString()} successful`}
            />
            <StatCard
              icon={<HeartPulse className="h-5 w-5 text-purple-500" />}
              label="p50 Latency"
              value={fmtMs(filteredTotals.p50Ms)}
              sub="Median — half of requests are faster"
            />
            <StatCard
              icon={<AlertCircle className="h-5 w-5 text-amber-500" />}
              label="p99 Latency"
              value={fmtMs(filteredTotals.p99Ms)}
              sub="Worst 1% — tail latency outliers"
            />
            <StatCard
              icon={<ArrowDownToLine className="h-5 w-5 text-sky-500" />}
              label="Bandwidth"
              value={fmtBytes(filteredTotals.bandwidthIngressBytes + filteredTotals.bandwidthEgressBytes)}
              sub={`↓ ${fmtBytes(filteredTotals.bandwidthIngressBytes)} · ↑ ${fmtBytes(filteredTotals.bandwidthEgressBytes)}`}
            />
          </div>

          {/* ── Charts row (excludes health check routes) ── */}
          <div className="grid gap-4 xl:grid-cols-2">
            <RequestRateChart history={rateHistory} />
            <RouteLatencyChart routes={filteredRoutes} />
          </div>

          {/* ── Route histogram inspector (excludes health check routes) ── */}
          <RouteHistogram routes={filteredRoutes} />

          {/* ── Route table ── */}
          <RouteTable routes={data.routes} />

          {/* ── Upstream health ── */}
          {data.upstreams.length > 0 && (
            <div className="card overflow-hidden">
              <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Upstream Health</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-white/5">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">
                        Upstream
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">
                        Target
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">
                        State
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">
                        Weight
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.upstreams.map((u, i) => (
                      <tr
                        key={i}
                        className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-900"
                      >
                        <td className="px-4 py-2 font-medium text-gray-700 dark:text-gray-300">{u.upstream}</td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">{u.target}</td>
                        <td className="px-4 py-2">
                          <UpstreamBadge state={u.state} />
                        </td>
                        <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{u.weight}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default KongHealth
